import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { CircularProgress, Grid, Stack } from '@mui/material';
import usePublisher from 'api/usePublisher';
import { EPublisherSolutionModelSubType, ETaskId } from 'common/contracts';
import { useFormik } from 'formik';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { required } from 'utils/errorsTextHelper';
import usePermissions from 'api/usePermissions';
import { permissionsUtil } from 'utils/permissionsUtil';
import * as yup from 'yup';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import AcPhoneInput from 'components/AcPhoneInput/AcPhoneInput';
import ActionButton from 'components/ActionButton/ActionButton';

import { EToasterStatus, showToast } from '../../../utils/showToast';

import 'style/forms.scss';

const CompanySettings = () => {
  const { publisherId } = useParams();
  const { isSuperAdmin } = usePermissions();
  const { getPublisher, updatePublisher } = usePublisher(
    publisherId || undefined
  );
  const { hasCategories, setTaskStatus, isWebStore, isWebCheckout } = useChecklistState();

  const data: any = getPublisher?.data;

  const isPaymentLinkProject =
    data?.solutionModelSubtype === EPublisherSolutionModelSubType.PAYMENT_LINKS;

  let companySettingsSchema = yup.object().shape({
    companyName: yup.string().required(required('Name')),
    phoneNumberPrefix: yup.string().max(3),
    supportMail: yup.string().email().required(required('Email')),
    address: yup.string(),
    domain: yup
      .string()
      .required(required('Domain'))
      .matches(
        /^(?!https?:\/\/)(?!.*\/$)[a-zA-Z0-9.-]+$/,
        "Please enter a valid domain name, without http://' or 'https://' prefixes or suffixes"
      ),
    phoneNumber: yup
      .string()
      .min(5, 'Phone number must be at least 5 characters')
  });

  const initialValues = useMemo(
    () => ({
      phoneNumberPrefix: data?.phoneNumberPrefix || '',
      companyName: data?.companyName || '',
      phoneNumber: data?.phoneNumber || '',
      supportMail: data?.supportMail || '',
      address: data?.address || '',
      domain: data?.domain || '',
      publisherId: data?._id || ''
    }),
    [data]
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    handleBlur,
    submitForm,
    isSubmitting,
    dirty,
    isValid
  } = useFormik({
    initialValues,
    validationSchema: companySettingsSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      savePublisherDetails(values, resetForm);
    }
  });

  const savePublisherDetails = (companyData: any, resetForm: () => void) => {
    updatePublisher.mutate(companyData, {
      onSuccess: async () => {
        showToast({
          message: 'Company details updated successfully',
          status: EToasterStatus.SUCCESS
        });
        if (hasCategories && (isWebStore || isWebCheckout)) {
          setTaskStatus(ETaskId.CUSTOMIZE_YOUR_DOMAIN);
        }
        await getPublisher.refetch();
        resetForm();
      },
      onError: (data: any) => {
        showToast({
          message: data.response.data.message,
          status: EToasterStatus.ERROR
        });
      }
    });
  };

  if (getPublisher.isLoading || !getPublisher.data) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 340px)',
          width: '100%'
        }}
      >
        <CircularProgress title="Loading..." />
      </div>
    );
  }

  return (
    <Grid container className="formContent">
      <Grid item xs={5} className="formContent-minWIdth">
        <Stack>
          <AcCard stackContainer={false} title="General">
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={6}>
                <AcInput
                  header="Project Name"
                  name="companyName"
                  value={values.companyName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.companyName && Boolean(errors.companyName)}
                  helperText={
                    touched.companyName ? errors.companyName?.toString() : ''
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <AcPhoneInput
                  header="Phone Number"
                  names={['phoneNumberPrefix', 'phoneNumber']}
                  placeholders={['eg. 1', 'eg. 0508242111']}
                  values={[values.phoneNumberPrefix, values.phoneNumber]}
                  setFieldValue={setFieldValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errors={[
                    touched.phoneNumberPrefix &&
                      Boolean(errors.phoneNumberPrefix),
                    touched.phoneNumber && Boolean(errors.phoneNumber)
                  ]}
                  helpers={[
                    touched.phoneNumberPrefix
                      ? errors.phoneNumberPrefix?.toString()
                      : '',
                    touched.phoneNumber ? errors.phoneNumber?.toString() : ''
                  ]}
                />
              </Grid>
              <Grid item xs={6}>
                <AcInput
                  header="Project ID"
                  disabled
                  name="publisherId"
                  value={data?._id}
                />
              </Grid>
              <Grid item xs={6}>
                <AcInput
                  header="Support Email"
                  name="supportMail"
                  value={values.supportMail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    data?.supportModel === 'Publisher' &&
                    touched.supportMail &&
                    Boolean(errors.supportMail)
                  }
                  helperText={
                    data?.supportModel !== 'Publisher'
                      ? 'Your support model directs support cases to Appcharge. To change your support model, please contact Appcharge.'
                      : touched.supportMail
                        ? errors.supportMail?.toString()
                        : ''
                  }
                  disabled={data?.supportModel !== 'Publisher'}
                />
              </Grid>
              <Grid item xs={6}>
                <AcInput
                  header="Address"
                  name="address"
                  value={values.address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.address && Boolean(errors.address)}
                  helperText={touched.address ? errors.address?.toString() : ''}
                />
              </Grid>
              {!isPaymentLinkProject && (
                <Grid item xs={6}>
                  <AcInput
                    header="Domain"
                    name="domain"
                    tooltip="Enter a domain name (e.g., example.com ) without 'http://' or 'https://'"
                    value={values.domain}
                    disabled={!isSuperAdmin}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.domain && Boolean(errors.domain)}
                    helperText={touched.domain ? errors.domain?.toString() : ''}
                    enableNewTabOpen
                  />
                </Grid>
              )}
            </Grid>
          </AcCard>
        </Stack>
        <Grid item xs={6} mt={3}>
          <ActionButton
            variant="outlined"
            text="Update"
            onClick={submitForm}
            disabled={!isValid || !dirty || isSubmitting}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CompanySettings;
